<template>  
  <div  
    :class="[isLessonEnded || isLessonTerminated ? 'timer_text' : 'header_time']"  
    v-if="!hideTimer"  
  >   
    <div class="lesson-datetime" :class="{ 'is-sole': isWaitingOrWarmUpPeriod, 'hide-on-sp': !isWaitingOrWarmUpPeriod && !isLessonEndedOrTerminated }" v-if="!isLessonEndedOrTerminated">
      <span class="ld-label">{{ $t('lessonTimer.lessonDateTimeLabel') }}</span> <span class="ld-date">{{ lessonDate }}</span><span class="ld-time">{{ lessonTimeRange }}</span>
    </div>
   
    <div class="timer-remaining" v-if="!isWaitingOrWarmUpPeriod && !isLessonEndedOrTerminated">
      <span class="timer-sub-label">{{ $t('lessonTimer.timerLabel') }}</span>
      <span class="timer-countdown" :class="timerSpanClass">{{ timer }}</span>
    </div>

    <span v-if="isLessonEndedOrTerminated">
      {{ $t('lessonTimer.lessonHasEnded') }}
    </span>
  </div>  
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { tsNow } from '@/utils/timeSync';
import { store } from '@/utils/common';

import gracePeriodMixin from '@/mixins/gracePeriod';
import chatboxMixin from '@/mixins/chatbox';
import userLocalTimezoneMixin from '@/mixins/userLocalTimezone';

export default {
  mixins: [gracePeriodMixin, chatboxMixin, userLocalTimezoneMixin],
  data() {
    return {
      timer: '00:00',
      timerInterval: null,
      durationExceed: false,
      studentTimer: true,
      hideTimer: false
    };
  },
  watch: {
    isLessonEndedOrTerminated: {
      immediate: true,
      handler(status) {
        if (status) {
          this.setIsLessonHasEnded(true);
          if (this.isStudent) store.setTranscribeState(this.lessonId, false);
          if (this.isStudent) store.setAgoraRecordState(this.lessonId, false);
        }
      }
    },
    isLessonBecameOngoing(status) {
      if (status) {
        this.lessonTimer();
      }
    },
    isLessonWithinGracePeriod(status) {
      if (status && this.isTrainer) {
        this.durationExceed = true;
      }
      if (status) {
        this.$logger.info('Sending last batch of info logs', { operation: 'send-batch-log' });
      }
    }
  },
  mounted() {
    if ((this.isLessonOngoing || this.isEnableChatbox()) && !this.isLessonEnded) {
      this.lessonTimer();
    }
  },
  computed: {
    ...mapState([
      'lessonStart',
      'lessonEnd',
      'accType',
      'notifyPeriod',
      'lessonId'
    ]),
    ...mapState('lesson', ['isLessonBecameOngoing']),
    ...mapState('connection', ['canReconnect']),
    ...mapState('socketio', ['lessonResultCode', 'isTimeSyncReceived']),
    ...mapGetters('socketio', [
      'hadTrainerSubstitution',
      'unterminatedLessonResult',
      'hasNextLesson'
    ]),
    ...mapGetters([
      'isTrainer',
      'isStudent',
      'isLessonOngoing',
      'isLessonEnded',
      'isWaitingOrWarmUpPeriod'
    ]),

    isLessonEndedOrTerminated() {
      return this.isLessonEnded || this.isLessonTerminated;
    },

    lessonDate() {
      const start = this.toLocal(this.lessonStart);
      const date = start.dateObject.format(this.$t('lessonTimer.lessonDateFormat'));
      const dow = start.dateObject.locale(this.$i18n.locale).format('ddd');
      const dowText = this.$i18n.locale === 'ja' ? ` （${dow}）` : ` (${dow}) `;
      return `${date}${dowText}`;
    },
    lessonTimeRange() {
      const start = this.toLocal(this.lessonStart);
      const end = this.toLocal(this.lessonEnd);
      return `${start.time}〜${end.time}`;
    },

    timerSpanClass() {
      if (this.isLessonEnded) {
        return null;
      }

      if (this.durationExceed) {
        return 'text-red';
      }

      if (this.isWaitingOrWarmUpPeriod) {
        return 'text-blue-500';
      }

      return null;
    },

    isLessonTerminated() {
      return (this.lessonResultCode && !this.unterminatedLessonResult && !this.hadTrainerSubstitution);
    }
  },
  methods: {
    ...mapMutations('lesson', ['setIsLessonHasEnded']),
    lessonTimer() {
      this.timerInterval = setInterval(() => {
        // gracePeriod feature
        if (this.isTimeSyncReceived) {
          this.autoDisconnect();
        }

        this.setTimerLabel();
        
        // Chatbox availability
        if (!this.isEnableChatbox()) {
          this.disconnectSocket();
        }

        if (!this.canReconnect && !this.isEnableChatbox()) {
          clearInterval(this.timerInterval);
        }
      }, 1000);
    },

    // Set Lesson Timer Label
    setTimerLabel() {
      let lessonTimeLeft = Math.ceil(this.lessonEnd - tsNow() / 1000);

      if ((this.isTrainer && this.hasNextLesson) && lessonTimeLeft <= 0) {
        let notifyLeft = Math.ceil(this.notifyPeriod - tsNow() / 1000);

        if (notifyLeft == 0) {
          this.$notify({
            title: 'Notice',
            message:this.$t('lessonTimer.nextLessonMessage'),
            type: 'warning',
            position: 'top-left',
            duration: 15000
          });
        }
      }

      if (this.isTrainer || this.studentTimer) {
        this.timer = this.getTimerLabel(lessonTimeLeft);
        this.studentTimer = !this.isTimeSyncReceived || lessonTimeLeft > 0;
      }
    },

    // Get timer label and check if it's in correct time (not advanced)
    getTimerLabel(lessonTimeLeft) {
      const timer = this.secondsToHHMMSS(lessonTimeLeft);
      this.hideTimer = !this.isTimeSyncReceived && lessonTimeLeft < 0;

      return lessonTimeLeft < 0 ? `-${timer}` : timer;
    },

    // Convert/Parse seconds to hh:mm format
    secondsToHHMMSS(secs) {
      let sec_num = Math.abs(secs);
      let hours = Math.floor(sec_num / 3600);
      let minutes = Math.floor(sec_num / 60) % 60;
      let seconds = sec_num % 60;

      return [hours, minutes, seconds]
        .map(v => (v < 10 ? '0' + v : v))
        .filter((v, i) => v !== '00' || i > 0)
        .join(':');
    }
  }
};
</script>
